// ============================================
// GESTÃO DE USUÁRIOS - ADMIN
// src/pages/admin/AdminUsuarios.tsx
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { API_CONFIG } from '../../services/staticDataConfig';

interface Usuario {
  id: number;
  cod_empresa: number;
  nome_empresa: string;
  nome: string;
  email: string;
  usuario: string;
  cargo: string;
  ativo: boolean;
  data_cadastro: string;
  ultimo_login: string;
  total_consultas: number;
  total_pacientes: number;
}

function normalizarUsuario(raw: any): Usuario {
  return {
    id: raw?.id ?? 0,
    cod_empresa: raw?.cod_empresa ?? raw?.codEmpresa ?? 0,
    nome_empresa: raw?.nome_empresa ?? raw?.empresaNome ?? raw?.empresa_nome ?? '',
    nome: raw?.nome ?? '',
    email: raw?.email ?? '',
    usuario: raw?.usuario ?? '',
    cargo: raw?.cargo ?? '',
    ativo: raw?.ativo ?? true,
    data_cadastro: raw?.data_cadastro ?? raw?.criadoEm ?? raw?.created_at ?? raw?.createdAt ?? '',
    ultimo_login: raw?.ultimo_login ?? raw?.ultimoLogin ?? '',
    total_consultas: raw?.total_consultas ?? raw?.totalConsultas ?? 0,
    total_pacientes: raw?.total_pacientes ?? raw?.totalPacientes ?? 0
  };
}

interface Paginacao {
  pagina: number;
  limite: number;
  total: number;
  totalPaginas: number;
}

interface Filtros {
  busca: string;
  status: 'todos' | 'ativo' | 'inativo';
  cargo: string;
  codEmpresa: string;
}

export function AdminUsuarios() {
  const { token, hasPermission } = useAdminAuth();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [paginacao, setPaginacao] = useState<Paginacao>({
    pagina: 1,
    limite: 20,
    total: 0,
    totalPaginas: 0
  });
  const [filtros, setFiltros] = useState<Filtros>({
    busca: '',
    status: 'todos',
    cargo: '',
    codEmpresa: ''
  });
  const [usuarioSelecionado, setUsuarioSelecionado] = useState<Usuario | null>(null);
  // const [showModalDetalhes, setShowModalDetalhes] = useState(false); // TODO: implementar modal detalhes
  const [showModalResetSenha, setShowModalResetSenha] = useState(false);
  const [novaSenha, setNovaSenha] = useState('');
  const [resetandoSenha, setResetandoSenha] = useState(false);
  const [ordenacao, setOrdenacao] = useState<{
    campo: keyof Usuario | null;
    direcao: 'asc' | 'desc';
  }>({ campo: null, direcao: 'asc' });

  const carregarUsuarios = useCallback(async () => {
    try {
      setLoading(true);
      setErro(null);

      const params = new URLSearchParams();
      params.append('pagina', paginacao.pagina.toString());
      params.append('limite', paginacao.limite.toString());
      
      if (filtros.busca) params.append('busca', filtros.busca);
      if (filtros.status !== 'todos') params.append('status', filtros.status);
      if (filtros.cargo) params.append('cargo', filtros.cargo);
      if (filtros.codEmpresa) params.append('codEmpresa', filtros.codEmpresa);
      
      // Ordenação server-side
      if (ordenacao.campo) {
        // Mapear campos frontend -> backend
        const campoMap: Record<string, string> = {
          'id': 'id',
          'nome': 'nome',
          'nome_empresa': 'empresaNome',
          'cargo': 'cargo',
          'total_pacientes': 'totalPacientes',
          'total_consultas': 'totalConsultas',
          'ultimo_login': 'ultimoLogin',
          'data_cadastro': 'criadoEm',
          'ativo': 'ativo'
        };
        const campoBackend = campoMap[ordenacao.campo] || ordenacao.campo;
        params.append('ordenarPor', campoBackend);
        params.append('ordem', ordenacao.direcao.toUpperCase());
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/usuarios?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar usuários');
      }

      const data = await response.json();
      
      if (data.sucesso) {
        const lista = Array.isArray(data.dados) ? data.dados : [];
        setUsuarios(lista.map(normalizarUsuario));
        setPaginacao(prev => ({
          ...prev,
          total: data.paginacao?.total || 0,
          totalPaginas: data.paginacao?.totalPaginas || 0
        }));
      }
    } catch (error) {
      console.error('[AdminUsuarios] Erro:', error);
      setErro('Erro ao carregar lista de usuários');
    } finally {
      setLoading(false);
    }
  }, [token, paginacao.pagina, paginacao.limite, filtros, ordenacao]);

  useEffect(() => {
    carregarUsuarios();
  }, [carregarUsuarios]);

  async function alterarStatusUsuario(usuarioId: number, novoStatus: boolean) {
    if (!hasPermission('gerenciar_usuarios')) {
      setErro('Você não tem permissão para alterar status de usuários');
      return;
    }

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/usuarios/${usuarioId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ativo: novoStatus })
      });

      if (!response.ok) {
        throw new Error('Erro ao alterar status do usuário');
      }

      const data = await response.json();
      
      if (data.sucesso) {
        setSucesso(`Status do usuário alterado para ${novoStatus ? 'ativo' : 'inativo'}`);
        carregarUsuarios();
        setTimeout(() => setSucesso(null), 3000);
      }
    } catch (error) {
      console.error('[AdminUsuarios] Erro ao alterar status:', error);
      setErro('Erro ao alterar status do usuário');
    }
  }

  async function resetarSenha() {
    if (!usuarioSelecionado || !novaSenha.trim()) return;

    if (!hasPermission('resetar_senhas')) {
      setErro('Você não tem permissão para resetar senhas');
      return;
    }

    if (novaSenha.length < 8) {
      setErro('A senha deve ter pelo menos 8 caracteres');
      return;
    }

    try {
      setResetandoSenha(true);
      setErro(null);

      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/usuarios/${usuarioSelecionado.id}/resetar-senha`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ novaSenha })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao resetar senha');
      }

      const data = await response.json();
      
      if (data.sucesso) {
        setSucesso(`Senha do usuário ${usuarioSelecionado.nome} foi resetada com sucesso`);
        setShowModalResetSenha(false);
        setNovaSenha('');
        setUsuarioSelecionado(null);
        setTimeout(() => setSucesso(null), 5000);
      }
    } catch (error) {
      console.error('[AdminUsuarios] Erro ao resetar senha:', error);
      setErro(error instanceof Error ? error.message : 'Erro ao resetar senha');
    } finally {
      setResetandoSenha(false);
    }
  }

  function abrirModalResetSenha(usuario: Usuario) {
    setUsuarioSelecionado(usuario);
    setNovaSenha('');
    setShowModalResetSenha(true);
  }

  function handleFiltroChange(campo: keyof Filtros, valor: string) {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
    setPaginacao(prev => ({ ...prev, pagina: 1 }));
  }

  function limparFiltros() {
    setFiltros({
      busca: '',
      status: 'todos',
      cargo: '',
      codEmpresa: ''
    });
    setPaginacao(prev => ({ ...prev, pagina: 1 }));
  }

  // Função formatarData disponível para uso futuro em modal de detalhes
  // function formatarData(dataString: string): string {
  //   if (!dataString) return '-';
  //   try {
  //     const data = new Date(dataString);
  //     return data.toLocaleDateString('pt-BR');
  //   } catch {
  //     return dataString;
  //   }
  // }

  function formatarDataHora(dataString: string): string {
    if (!dataString) return 'Nunca';
    try {
      const data = new Date(dataString);
      return data.toLocaleString('pt-BR');
    } catch {
      return dataString;
    }
  }

  function gerarSenhaAleatoria(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#$!';
    let senha = '';
    for (let i = 0; i < 12; i++) {
      senha += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return senha;
  }

  function ordenarPor(campo: keyof Usuario) {
    setOrdenacao(prev => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc'
    }));
    // Voltar para página 1 ao mudar ordenação
    setPaginacao(prev => ({ ...prev, pagina: 1 }));
  }

  // Ordenação agora é feita no backend, apenas usar usuarios direto
  const usuariosOrdenados = usuarios;

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="fas fa-users me-2"></i>
            Gestão de Usuários
          </h2>
          <p className="text-muted mb-0">
            {paginacao.total} usuário{paginacao.total !== 1 ? 's' : ''} encontrado{paginacao.total !== 1 ? 's' : ''}
          </p>
        </div>
        <button 
          className="btn btn-outline-primary"
          onClick={carregarUsuarios}
          disabled={loading}
        >
          <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} me-2`}></i>
          Atualizar
        </button>
      </div>

      {/* Alertas */}
      {erro && (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          <div>{erro}</div>
          <button type="button" className="btn-close ms-auto" onClick={() => setErro(null)}></button>
        </div>
      )}
      
      {sucesso && (
        <div className="alert alert-success d-flex align-items-center" role="alert">
          <i className="fas fa-check-circle me-2"></i>
          <div>{sucesso}</div>
          <button type="button" className="btn-close ms-auto" onClick={() => setSucesso(null)}></button>
        </div>
      )}

      {/* Filtros */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Buscar</label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nome, email ou usuário..."
                  value={filtros.busca}
                  onChange={(e) => handleFiltroChange('busca', e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filtros.status}
                onChange={(e) => handleFiltroChange('status', e.target.value as Filtros['status'])}
              >
                <option value="todos">Todos</option>
                <option value="ativo">Ativos</option>
                <option value="inativo">Inativos</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Cargo</label>
              <select
                className="form-select"
                value={filtros.cargo}
                onChange={(e) => handleFiltroChange('cargo', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="admin">Admin</option>
                <option value="psicologo">Psicólogo</option>
                <option value="secretaria">Secretária</option>
                <option value="estudante">Estudante</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">ID Empresa</label>
              <input
                type="number"
                className="form-control"
                placeholder="Código"
                value={filtros.codEmpresa}
                onChange={(e) => handleFiltroChange('codEmpresa', e.target.value)}
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button 
                className="btn btn-outline-secondary w-100"
                onClick={limparFiltros}
              >
                <i className="fas fa-times me-2"></i>
                Limpar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Usuários */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => ordenarPor('id')}>
                    ID {ordenacao.campo === 'id' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => ordenarPor('nome')}>
                    Usuário {ordenacao.campo === 'nome' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => ordenarPor('nome_empresa')}>
                    Empresa {ordenacao.campo === 'nome_empresa' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => ordenarPor('cargo')}>
                    Cargo {ordenacao.campo === 'cargo' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => ordenarPor('total_pacientes')}>
                    Pacientes {ordenacao.campo === 'total_pacientes' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => ordenarPor('total_consultas')}>
                    Consultas {ordenacao.campo === 'total_consultas' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => ordenarPor('ultimo_login')}>
                    Último Login {ordenacao.campo === 'ultimo_login' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => ordenarPor('ativo')}>
                    Status {ordenacao.campo === 'ativo' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading && usuariosOrdenados.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                    </td>
                  </tr>
                ) : usuariosOrdenados.length > 0 ? (
                  usuariosOrdenados.map((usuario) => (
                    <tr key={usuario.id}>
                      <td>
                        <span className="badge bg-secondary">{usuario.id}</span>
                      </td>
                      <td>
                        <strong>{usuario.nome}</strong>
                        <br />
                        <small className="text-muted">
                          <i className="fas fa-at me-1"></i>
                          {usuario.usuario}
                        </small>
                        <br />
                        <small className="text-muted">
                          <i className="fas fa-envelope me-1"></i>
                          {usuario.email}
                        </small>
                      </td>
                      <td>
                        <span className="badge bg-info">{usuario.cod_empresa}</span>
                        <br />
                        <small className="text-muted">{usuario.nome_empresa}</small>
                      </td>
                      <td>
                        <span className={`badge ${
                          usuario.cargo === 'admin' ? 'bg-danger' :
                          usuario.cargo === 'psicologo' ? 'bg-primary' :
                          usuario.cargo === 'secretaria' ? 'bg-warning text-dark' :
                          'bg-secondary'
                        }`}>
                          {usuario.cargo}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-light text-dark">
                          {usuario.total_pacientes}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-light text-dark">
                          {usuario.total_consultas}
                        </span>
                      </td>
                      <td>
                        <small className="text-muted">
                          {formatarDataHora(usuario.ultimo_login)}
                        </small>
                      </td>
                      <td>
                        <span className={`badge ${usuario.ativo ? 'bg-success' : 'bg-danger'}`}>
                          {usuario.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          {hasPermission('resetar_senhas') && (
                            <button 
                              className="btn btn-outline-warning"
                              title="Resetar senha"
                              onClick={() => abrirModalResetSenha(usuario)}
                            >
                              <i className="fas fa-key"></i>
                            </button>
                          )}
                          {hasPermission('gerenciar_usuarios') && (
                            <button 
                              className={`btn ${usuario.ativo ? 'btn-outline-danger' : 'btn-outline-success'}`}
                              title={usuario.ativo ? 'Desativar' : 'Ativar'}
                              onClick={() => alterarStatusUsuario(usuario.id, !usuario.ativo)}
                            >
                              <i className={`fas fa-${usuario.ativo ? 'ban' : 'check'}`}></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center py-5">
                      <i className="fas fa-users-slash fa-3x text-muted mb-3 d-block"></i>
                      <p className="text-muted mb-0">Nenhum usuário encontrado</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Paginação */}
        {paginacao.totalPaginas > 1 && (
          <div className="card-footer bg-transparent">
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                Mostrando {((paginacao.pagina - 1) * paginacao.limite) + 1} a{' '}
                {Math.min(paginacao.pagina * paginacao.limite, paginacao.total)} de{' '}
                {paginacao.total} registros
              </small>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${paginacao.pagina === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => setPaginacao(prev => ({ ...prev, pagina: prev.pagina - 1 }))}
                    >
                      Anterior
                    </button>
                  </li>
                  {Array.from({ length: Math.min(5, paginacao.totalPaginas) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <li key={pageNum} className={`page-item ${paginacao.pagina === pageNum ? 'active' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setPaginacao(prev => ({ ...prev, pagina: pageNum }))}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${paginacao.pagina === paginacao.totalPaginas ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => setPaginacao(prev => ({ ...prev, pagina: prev.pagina + 1 }))}
                    >
                      Próximo
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Modal Reset de Senha */}
      {showModalResetSenha && usuarioSelecionado && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-key text-warning me-2"></i>
                  Resetar Senha
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowModalResetSenha(false)}
                  disabled={resetandoSenha}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>Atenção:</strong> Você está prestes a resetar a senha do usuário.
                </div>
                
                <div className="mb-3">
                  <label className="form-label text-muted">Usuário</label>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-user text-muted me-2"></i>
                    <strong>{usuarioSelecionado.nome}</strong>
                  </div>
                  <small className="text-muted">
                    @{usuarioSelecionado.usuario} • {usuarioSelecionado.email}
                  </small>
                </div>

                <div className="mb-3">
                  <label htmlFor="novaSenha" className="form-label">Nova Senha</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="novaSenha"
                      value={novaSenha}
                      onChange={(e) => setNovaSenha(e.target.value)}
                      placeholder="Digite a nova senha"
                      disabled={resetandoSenha}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setNovaSenha(gerarSenhaAleatoria())}
                      disabled={resetandoSenha}
                      title="Gerar senha aleatória"
                    >
                      <i className="fas fa-random"></i>
                    </button>
                  </div>
                  <small className="text-muted">Mínimo 8 caracteres</small>
                </div>

                {novaSenha && (
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    Informe esta senha ao usuário: <strong className="user-select-all">{novaSenha}</strong>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowModalResetSenha(false)}
                  disabled={resetandoSenha}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="btn btn-warning"
                  onClick={resetarSenha}
                  disabled={resetandoSenha || !novaSenha.trim() || novaSenha.length < 8}
                >
                  {resetandoSenha ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Resetando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-key me-2"></i>
                      Resetar Senha
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsuarios;
